import { EnumMapper, Failure, Success } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import type { DiceRoll, WeaponDamage } from "../engine/common.ts";
import type { VehicleWeaponRef, WeaponProfile } from "../engine/Ruleset.ts";

export const BuildMode = EnumMapper.from<SpaceGits.GangModeData["type"]>({
  A_LA_CARTE: "A La Carte",
  CHEFS_REC: "Chef's Recommendation",
  CHAOS_MODE: "Chaos Mode",
  CAMPAIGN: "Campaign",
});

export function signedNumber(value: number) {
  if (value > 0) {
    return `+${value}`;
  }

  if (value < 0) {
    return `${value}`;
  }

  return value;
}

export function compactDamage(value: string) {
  if (value === "Explode") {
    return "Expl.";
  }

  return value;
}

export function damage(damage: WeaponDamage) {
  if (damage.special) {
    return "Explode";
  }

  if (damage.big === 0 && damage.weak === 0) {
    return "-";
  }

  return `${damage.weak}/${damage.big}`;
}

export function weaponStats(w: WeaponProfile) {
  const base = `Dice: ${diceRoll(w.dice)} · Damage: ${damage(w.damage)}`;

  if (w.range === 0) {
    return base;
  }

  return `Range: ${w.range}″ · ${base}`;
}

function diceRoll(value: DiceRoll, options?: { style?: "short" }) {
  if (options?.style && value.count === 1) {
    return value.type;
  }

  return `${value.count}${value.type}`;
}

export function diceRolls(diceRollOrRolls: DiceRoll | DiceRoll[]) {
  if (Array.isArray(diceRollOrRolls)) {
    return diceRollOrRolls
      .map((roll) => diceRoll(roll, { style: "short" }))
      .join(", ");
  }

  return diceRoll(diceRollOrRolls);
}

export function caps(caps: number, options?: { context: "cost" | "stash" }) {
  const { context = "cost" } = options ?? {};

  if (context === "cost" && caps === 0) {
    return `free`;
  }

  return pluralize(caps, { one: "1\u00A0cap", other: "#\u00A0caps" });
}

export function hands(hands: number) {
  if (hands === 0) {
    return "hands free";
  }

  return pluralize(hands, { one: "1\u00A0hand", other: "#\u00A0hands" });
}

export function placement(ref: VehicleWeaponRef) {
  switch (ref.placement) {
    case "FIXED": {
      return "fixed forward";
    }

    case "PINTLE_MOUNTED": {
      return "pintle mounted";
    }

    default: {
      return "unknown";
    }
  }
}

const cardinalRules = new Intl.PluralRules("en-GB");

// English only uses "one" and "other"
type EnglishCardinalRule = Extract<Intl.LDMLPluralRule, "one" | "other">;

export function pluralize(
  count: number,
  rules: Record<EnglishCardinalRule, string>,
) {
  const tag = cardinalRules.select(count) as EnglishCardinalRule;
  return rules[tag].replace("#", count.toString());
}

type NamedItem = {
  name: string;
};

export type ListItem =
  | string
  | null
  | undefined
  | NamedItem
  | Success<NamedItem>
  | Success<string>
  | Failure<unknown>;

type CountedItem = {
  name: string;
  count: number;
};

function countedItem(item: CountedItem) {
  return item.count > 1 ? `${item.name}\u00A0x${item.count}` : item.name;
}

type CountedItemResult = Success<CountedItem> | Failure<unknown>;

export function aggregateWithCounts(items: CountedItemResult[]) {
  return items.map((item) => item.mapSuccess((value) => countedItem(value)));
}

export function toCountedList(items: { name: string; count?: number }[]) {
  const seen = new Map<string, CountedItem>();

  for (const item of items) {
    const seenItem = seen.get(item.name);

    seen.set(item.name, {
      name: item.name,
      count: (seenItem?.count ?? 0) + (item.count ?? 1),
    });
  }

  return Array.from(seen.values());
}

export function listWithCounts(items: CountedItem[]) {
  return list(items.map((item) => countedItem(item)));
}

export function list(items: ListItem[]) {
  type Excluded = null | undefined;

  return items
    .filter((item): item is Exclude<typeof item, Excluded> => {
      return !!item;
    })
    .map((item) => {
      return item instanceof Success
        ? typeof item.value === "string"
          ? item.value
          : item.value.name
        : item instanceof Failure
          ? "Unknown"
          : typeof item === "object"
            ? item.name
            : item;
    })
    .join(", ");
}

export function dateTime(date: string | number) {
  return new Date(date).toLocaleString();
}

const conjunctionFmt = new Intl.ListFormat("en-GB", {
  style: "long",
  type: "conjunction",
});

export function conjunction(items: string[]) {
  return conjunctionFmt.format(items);
}

export const FeatureEnum = EnumMapper.from({
  SPACE_GITS_CORE_RULES: "Space Gits Core Rules",
});
